import { date, is } from 'quasar'
import type {
  DtdlMapSchema,
  DtdlObjectSchema,
  DtdlEnumSchema,
  DtdlPrimitiveSchema,
  DtdlSchema
} from 'models/Dtdl'
import type { DtdlLocalizedString } from 'models/DtdlLocalizableString'
import type { DtdlProperty } from 'models/DtdlProperty'
import type { SimpleProperty } from 'models/DtdlSimpleProperty'
import { getCommentValue } from 'utils/comments'
import { getPrimitiveType, isPrimitiveSchema, isString } from 'utils/type-checks'
import { getDisplayUnit } from 'utils/units'
import { currentLanguage } from 'utils/language'

const locale = navigator.languages[0]
const numberFormat = new Intl.NumberFormat(locale, { maximumFractionDigits: 2 })
const smallNumberFormat = new Intl.NumberFormat(locale, { maximumSignificantDigits: 2 })
const integerFormat = new Intl.NumberFormat(locale)

/** DTDL property types that can have display units */
type DtdlPropertyWithUnit = DtdlProperty | DtdlMapSchema['mapValue'] | DtdlObjectSchema['fields'][0]

export const formatNumber = (value: number, digits?: number): string => {
  if (digits !== undefined)
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits
    }).format(value)
  if (value !== 0 && Math.abs(value) < 0.01) return smallNumberFormat.format(value)
  return numberFormat.format(value)
}

/** Gets the display name in the provided language */
export const getDisplayName = (
  prop: SimpleProperty | undefined,
  lang?: string
): string | undefined => {
  if (!prop) return undefined
  const _lang = lang ?? currentLanguage.value
  const localizedDisplayName = prop.displayName as DtdlLocalizedString
  const _displayName =
    localizedDisplayName?.[_lang] ||
    localizedDisplayName?.en ||
    prop.displayName ||
    prop.name ||
    prop['@id']
  const full = isString(_displayName) ? _displayName : undefined
  // '|'-delimited format: first segment is always the primary label
  return full?.split('|')[0]
}

/** Returns all '|'-delimited label segments for a property, e.g. ["SSP1-2.6", "Low emissions scenario"] */
export const getDisplayNames = (prop: SimpleProperty | undefined, lang?: string): string[] => {
  if (!prop) return []
  const _lang = lang ?? currentLanguage.value
  const localizedDisplayName = prop.displayName as DtdlLocalizedString
  const _displayName =
    localizedDisplayName?.[_lang] ||
    localizedDisplayName?.en ||
    prop.displayName ||
    prop.name ||
    prop['@id']
  const full = isString(_displayName) ? _displayName : undefined
  return full ? full.split('|') : []
}

/** Gets the description in the provided language */
export const getDescription = (
  prop: SimpleProperty | undefined,
  lang?: string
): string | undefined => {
  if (!prop) return undefined
  const _lang = lang ?? currentLanguage.value
  const localizedDescription = prop.description as DtdlLocalizedString
  const _description = localizedDescription?.[_lang] || localizedDescription?.en || prop.description
  return isString(_description) ? _description : undefined
}

/** Gets the display unit from the provided property */
export const getDisplayUnitByProperty = (property: DtdlPropertyWithUnit) => {
  if (typeof (property as DtdlProperty).unit === 'string') {
    return getDisplayUnit((property as DtdlProperty).unit as string)
  } else {
    const commentUnit = getCommentValue(property.comment, 'unit')
    if (typeof commentUnit === 'string') return getDisplayUnit(commentUnit)
    return ''
  }
}

/**
 * Gets the formatted value by providing the full property object
 * @param value The value to format
 * @param property The property object
 */
export const getFormattedValueByProperty = (
  value: unknown,
  property?: DtdlProperty | DtdlMapSchema['mapValue'] | DtdlObjectSchema['fields'][0]
) => {
  if (value === undefined || value === null) {
    return ''
  } else {
    return getFormattedValueBySchema(value, property?.schema, property)
  }
}

/** Schema PrimitiveTypes */
const schemaPrimitiveTypes = {
  date: ['date', 'dateTime'],
  number: ['double', 'float', 'integer', 'long']
}

/** Gets the formatted value by providing the full property object or schema */
export const getFormattedValueBySchema = (
  value: unknown,
  schema?: DtdlSchema,
  property?: DtdlProperty | DtdlMapSchema['mapValue'] | DtdlObjectSchema['fields'][0]
): string => {
  if (value === undefined || value === null) return ''
  if (!schema) return `${getFormattedPrimitiveValueBySchema(value) || ''}`
  if (!property && !isPrimitiveSchema(schema))
    return `${getFormattedPrimitiveValueBySchema(value) || ''}`
  if (!property && isPrimitiveSchema(schema))
    return `${getFormattedPrimitiveValueBySchema(value, schema) || ''}`
  // TODO: add duration
  // TODO: add array
  if (property && isString(schema) && ['date', 'dateTime'].includes(schema)) {
    return `${getFormattedPrimitiveValueBySchema(value, schema)}`
  }
  if (
    property &&
    typeof value === 'number' &&
    typeof schema === 'string' &&
    schemaPrimitiveTypes.number.includes(schema)
  ) {
    // Add units
    return `${formatNumber(value)} ${getDisplayUnitByProperty(property) || ''}`
  }
  if (property && schema === 'boolean') {
    const trueValue = getCommentValue(property.comment, 'trueValue')
    const falseValue = getCommentValue(property.comment, 'falseValue')
    if (value && trueValue) return `${trueValue}`
    else if (value === false && falseValue) return `${falseValue}`
    else return `${value}`
  }
  if ((schema as DtdlEnumSchema)['@type'] === 'Enum') {
    const enumValue = (schema as DtdlEnumSchema)?.enumValues?.find((x) => x.enumValue === value)
    if (!enumValue) return `${value}`
    return getDisplayName(enumValue) || `${enumValue}`
  }
  if ((schema as DtdlMapSchema)['@type'] === 'Map' && is.object(value)) {
    if ((schema as DtdlMapSchema)?.mapValue?.schema === 'boolean') {
      return Object.entries(value as object)
        .filter((entry) => entry[1])
        .map((entry) => entry[0])
        .join(', ')
    } else {
      return Object.keys(value)
        .map(
          (key) =>
            key +
            ': ' +
            (typeof (value as Record<string, unknown>)[key] === 'object'
              ? JSON.stringify((value as Record<string, unknown>)[key])
              : (value as Record<string, unknown>)[key])
        )
        .join('\n')
    }
  }
  if ((schema as DtdlObjectSchema)['@type'] === 'Object' && is.object(value)) {
    return (schema as DtdlObjectSchema).fields
      .filter((x) => value[x.name])
      .map((field) => getDisplayName(field) + ': ' + value[field.name])
      .join('\n')
  }
  return `${value}`
}

/** Get the formatted (primitive) value only by the schema (doesn't support units or custom boolean values) */
export const getFormattedPrimitiveValueBySchema = (
  value: unknown,
  schema?: DtdlPrimitiveSchema | null,
  digits?: number
): string | unknown | undefined => {
  const _type = schema || getPrimitiveType(value)
  if (value === undefined || value === null) return undefined
  if (_type === undefined || _type === null) return value
  if (schemaPrimitiveTypes.number.includes(_type)) {
    if (['double', 'float'].includes(_type)) {
      // number to FLoat
      const _number: number = parseFloat(String(value))
      return formatNumber(_number, digits)
    } else {
      // number to Integer
      const _number: number = parseInt(String(value), 10)
      return integerFormat.format(_number)
    }
  }
  if (_type === 'date') {
    // date
    return new Date(value as string | number | Date).toLocaleDateString(navigator.languages[0]) // date.formatDate(new Date(value), 'DD/MM/YY')
  }
  if (_type === 'dateTime') {
    // dateTime
    return new Date(value as string | number | Date).toLocaleString(navigator.languages[0]) // date.formatDate(new Date(value), 'DD/MM/YY HH:mm')
  }
  if (_type === 'boolean') {
    // boolean
    return `${value}`
  }
  // TODO: add duration
  // return the raw (unknown) element
  return value
}

/** Get dummy value by schema*/
export const getDummyValueBySchema = (
  name: string,
  schema: DtdlSchema | string | undefined
): unknown => {
  if (schema === undefined || schema === null) {
    return undefined
  } else if (schema === 'date') {
    // date
    return date.formatDate(new Date(), 'YYYY-MM-DD')
  } else if (schema === 'dateTime') {
    // dateTime
    return new Date().toISOString()
  } else if (schema === 'time') {
    // time
    return date.formatDate(new Date(), 'HH:mm:ss.SSSZ')
  } else if (schema === 'boolean') {
    // boolean
    return true
  } else if (schema === 'double') {
    // double
    return 1.1
  } else if (schema === 'integer') {
    // integer
    return 1
  } else if (schema === 'string') {
    // string
    return name
  } else {
    return undefined
  }
}
